import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';
const LeaveApproval = () => {
  // State for leave requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  // State for filters
  const [filters, setFilters] = useState({
    status: 'All',
    department: 'All'
  });

  // State for search query
  const [searchQuery, setSearchQuery] = useState('');

  // State for departments
  const [departments, setDepartments] = useState(['All']);

  // State for selected request
  const [selectedRequest, setSelectedRequest] = useState(null);

  // State for department details modal
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  // State for rejection remarks modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectionRemarks, setRejectionRemarks] = useState('');

  // State for approved dates modal
  const [showApprovedDatesModal, setShowApprovedDatesModal] = useState(false);
  const [approvingRequestId, setApprovingRequestId] = useState(null);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [selectedApprovedDates, setSelectedApprovedDates] = useState([]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Fetch leave requests from API


  const fetchLeaveRequests = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
      setLoading(false);
      return;
    }

    // Extract companyId from token payload
    const companyId = decoded.companyId || decoded.id;
    if (!companyId) {
      setError("No companyId found in token.");
      setLoading(false);
      return;
    }

    try {
      // First, fetch all departments for the company to create a lookup map
      let departmentLookupMap = {};
      try {
        console.log("Fetching departments for company:", companyId);
        const departmentsResponse = await axios.get(
          `https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${companyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (departmentsResponse.data && Array.isArray(departmentsResponse.data)) {
          departmentLookupMap = departmentsResponse.data.reduce((map, dept) => {
            if (dept._id) {
              map[dept._id] = dept;
              console.log(`📋 Added to lookup: ${dept._id} -> ${dept.name || dept.departmentName}`);
            }
            return map;
          }, {});
          console.log("📋 Department lookup map created with", Object.keys(departmentLookupMap).length, "entries");
        } else {
          console.warn("⚠️ No departments array in response:", departmentsResponse.data);
        }
      } catch (deptErr) {
        console.error("⚠️ Error fetching departments for lookup:", deptErr);
        // Continue without department lookup map
      }

      const response = await axios.get("https://slateblue-mallard-430836.hostingersite.com/api/leaves", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: {
          company: companyId,
        },
      });

      console.log("Leave requests API response:", response.data);

      let leaves = [];
      if (response.data && Array.isArray(response.data)) {
        leaves = response.data;
      } else if (response.data.leaves && Array.isArray(response.data.leaves)) {
        leaves = response.data.leaves;
      }

      const formattedRequests = await Promise.all(
        leaves.map(async (leave) => {
          const fromDate = new Date(leave.fromDate);
          const toDate = new Date(leave.toDate);
          const timeDiff = toDate.getTime() - fromDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;

          const appliedDate = leave.appliedDate || leave.createdAt;
          const formattedAppliedDate = appliedDate
            ? new Date(appliedDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "N/A";

          let employeeDetails = null;

          if (leave.employee && (leave.employee._id || leave.employee)) {
            const employeeId = leave.employee._id || leave.employee;
            try {
              const employeeResponse = await axios.get(
                `https://slateblue-mallard-430836.hostingersite.com/api/employees/${employeeId}`,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              employeeDetails = employeeResponse.data;
              console.log(`Employee details for ${employeeId}:`, {
                name: employeeDetails?.teamMemberName || employeeDetails?.name,
                department: employeeDetails?.department,
                departments: employeeDetails?.departments
              });

              // Department fetching with better error handling and logging
              if (employeeDetails?.department) {
                console.log(`Processing department for employee ${employeeId}:`, employeeDetails.department);

                // Handle single department (string ID)
                if (typeof employeeDetails.department === "string") {
                  try {
                    let deptData = null;

                    // Try lookup map first
                    if (departmentLookupMap[employeeDetails.department]) {
                      deptData = departmentLookupMap[employeeDetails.department];
                      console.log(`✅ Found in lookup map: ${deptData.name || deptData.departmentName}`);
                    } else {
                      // Fetch individually
                      console.log(`„ Fetching department ${employeeDetails.department} individually`);
                      const deptResponse = await axios.get(
                        `https://slateblue-mallard-430836.hostingersite.com/api/departments/${employeeDetails.department}`,
                        {
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );
                      deptData = deptResponse.data;
                      console.log(`✅ Fetched department: ${deptData?.name || deptData?.departmentName}`);
                    }

                    employeeDetails.departmentDetails = deptData;
                  } catch (deptErr) {
                    console.error(`❌ Error fetching department ${employeeDetails.department}:`, deptErr);
                    // Create a fallback object
                    employeeDetails.departmentDetails = {
                      name: `Department ${employeeDetails.department}`,
                      _id: employeeDetails.department
                    };
                  }
                }
                // Handle multiple departments (array)
                else if (Array.isArray(employeeDetails.department)) {
                  console.log(`Processing multiple departments for employee ${employeeId}:`, employeeDetails.department);

                  try {
                    const departmentPromises = employeeDetails.department.map(async (deptId, index) => {
                      if (typeof deptId === "string") {
                        try {
                          let deptData = null;

                          // Try lookup map first
                          if (departmentLookupMap[deptId]) {
                            deptData = departmentLookupMap[deptId];
                            console.log(`✅ Dept ${index + 1} found in lookup: ${deptData.name || deptData.departmentName}`);
                          } else {
                            // Fetch individually
                            console.log(`„ Fetching dept ${index + 1} (${deptId}) individually`);
                            const deptResponse = await axios.get(
                              `https://slateblue-mallard-430836.hostingersite.com/api/departments/${deptId}`,
                              {
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`,
                                },
                              }
                            );
                            deptData = deptResponse.data;
                            console.log(`✅ Fetched dept ${index + 1}: ${deptData?.name || deptData?.departmentName}`);
                          }

                          return deptData || { name: `Department ${deptId}`, _id: deptId };
                        } catch (fetchErr) {
                          console.error(`❌ Error fetching dept ${deptId}:`, fetchErr);
                          return { name: `Department ${deptId}`, _id: deptId };
                        }
                      }
                      return deptId; // Already an object
                    });

                    const departmentDetails = await Promise.all(departmentPromises);
                    employeeDetails.departmentDetails = departmentDetails;
                    console.log(`✅ All departments fetched for employee ${employeeId}:`, departmentDetails.map(d => d?.name || d?.departmentName));
                  } catch (deptErr) {
                    console.error(`❌ Error fetching multiple departments:`, deptErr);
                    // Create fallback objects
                    employeeDetails.departmentDetails = employeeDetails.department.map(deptId => ({
                      name: `Department ${deptId}`,
                      _id: deptId
                    }));
                  }
                }
              }

              // Handle departments field (array format) - similar logic
              if (employeeDetails?.departments && Array.isArray(employeeDetails.departments)) {
                console.log(`Processing departments array for employee ${employeeId}:`, employeeDetails.departments);

                try {
                  const departmentPromises = employeeDetails.departments.map(async (dept, index) => {
                    if (typeof dept === "string") {
                      try {
                        let deptData = null;

                        // Try lookup map first
                        if (departmentLookupMap[dept]) {
                          deptData = departmentLookupMap[dept];
                          console.log(`✅ Dept array ${index + 1} found in lookup: ${deptData.name || deptData.departmentName}`);
                        } else {
                          // Fetch individually
                          console.log(`„ Fetching dept array ${index + 1} (${dept}) individually`);
                          const deptResponse = await axios.get(
                            `https://slateblue-mallard-430836.hostingersite.com/api/departments/${dept}`,
                            {
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                              },
                            }
                          );
                          deptData = deptResponse.data;
                          console.log(`✅ Fetched dept array ${index + 1}: ${deptData?.name || deptData?.departmentName}`);
                        }

                        return deptData || { name: `Department ${dept}`, _id: dept };
                      } catch (fetchErr) {
                        console.error(`❌ Error fetching dept array ${dept}:`, fetchErr);
                        return { name: `Department ${dept}`, _id: dept };
                      }
                    }
                    return dept; // Already an object
                  });

                  const departmentDetails = await Promise.all(departmentPromises);
                  employeeDetails.departmentsDetails = departmentDetails;
                  console.log(`✅ All departments array fetched for employee ${employeeId}:`, departmentDetails.map(d => d?.name || d?.departmentName));
                } catch (deptErr) {
                  console.error(`❌ Error fetching departments array:`, deptErr);
                  // Create fallback objects
                  employeeDetails.departmentsDetails = employeeDetails.departments.map(dept => ({
                    name: `Department ${dept}`,
                    _id: dept
                  }));
                }
              }
            } catch (employeeErr) {
              console.error(`❌ Error fetching employee ${employeeId}:`, employeeErr);
            }
          }

          return {
            id: leave._id,
            teamMemberName:
              employeeDetails?.teamMemberName ||
              employeeDetails?.name ||
              leave.employee?.teamMemberName ||
              leave.employee?.name ||
              (employeeDetails?.firstName && employeeDetails?.lastName
                ? `${employeeDetails.firstName} ${employeeDetails.lastName}`
                : leave.employee?.firstName && leave.employee?.lastName
                  ? `${leave.employee.firstName} ${leave.employee.lastName}`
                  : "Unknown Employee"),
            employeeId:
              employeeDetails?._id || leave.employee?._id || "N/A",
            department: (() => {
              console.log(`Resolving department for employee ${employeeDetails?._id || leave.employee?._id}:`);

              // Prioritize fetched department details
              if (employeeDetails?.departmentDetails) {
                console.log(`📋 Using departmentDetails:`, employeeDetails.departmentDetails);

                if (Array.isArray(employeeDetails.departmentDetails)) {
                  // Multiple departments - show only first department name
                  const deptNames = employeeDetails.departmentDetails
                    .map(dept => dept?.name || dept?.departmentName)
                    .filter(name => name && name.trim() !== '');
                  if (deptNames.length > 0) {
                    console.log(`✅ Selected first department: ${deptNames[0]}`);
                    return deptNames[0];
                  }
                } else {
                  // Single department
                  const deptName = employeeDetails.departmentDetails.name ||
                    employeeDetails.departmentDetails.departmentName;
                  if (deptName) {
                    console.log(`✅ Single department: ${deptName}`);
                    return deptName;
                  }
                }
              }

              // Handle departments array with fetched details
              if (employeeDetails?.departmentsDetails && Array.isArray(employeeDetails.departmentsDetails)) {
                console.log(`📋 Using departmentsDetails:`, employeeDetails.departmentsDetails);

                const deptNames = employeeDetails.departmentsDetails
                  .map(dept => dept?.name || dept?.departmentName)
                  .filter(name => name && name.trim() !== '');
                if (deptNames.length > 0) {
                  console.log(`✅ Selected first department from array: ${deptNames[0]}`);
                  return deptNames[0];
                }
              }

              // Fallback to lookup map only if no fetched details
              if (employeeDetails?.department) {
                console.log(`„ Falling back to lookup map for:`, employeeDetails.department);

                if (Array.isArray(employeeDetails.department)) {
                  const deptNames = employeeDetails.department
                    .map(deptId => {
                      if (typeof deptId === 'string' && departmentLookupMap[deptId]) {
                        return departmentLookupMap[deptId].name || departmentLookupMap[deptId].departmentName;
                      }
                      return null;
                    })
                    .filter(name => name && name.trim() !== '');
                  if (deptNames.length > 0) {
                    console.log(`✅ Found in lookup: ${deptNames[0]}`);
                    return deptNames[0];
                  }
                } else if (typeof employeeDetails.department === 'string' && departmentLookupMap[employeeDetails.department]) {
                  const deptName = departmentLookupMap[employeeDetails.department].name || departmentLookupMap[employeeDetails.department].departmentName;
                  console.log(`✅ Found single in lookup: ${deptName}`);
                  return deptName;
                }
              }

              // Check if employee has department field but no details fetched
              if (employeeDetails?.department) {
                console.log(`⚠️ Department field exists but no details fetched:`, employeeDetails.department);

                if (Array.isArray(employeeDetails.department) && employeeDetails.department.length > 0) {
                  console.log(`✅ Using first department ID as name: ${employeeDetails.department[0]}`);
                  return `Department ${employeeDetails.department[0]}`;
                } else if (typeof employeeDetails.department === 'string') {
                  console.log(`✅ Using department ID as name: ${employeeDetails.department}`);
                  return `Department ${employeeDetails.department}`;
                }
              }

              console.log(`❌ No department found, using Unknown Department`);
              return "Unknown Department";
            })(),
            // Store whether this employee has multiple departments for show more button
            hasMultipleDepartments: (() => {
              let deptCount = 0;

              if (employeeDetails?.departmentDetails && Array.isArray(employeeDetails.departmentDetails)) {
                deptCount = employeeDetails.departmentDetails.length;
              } else if (employeeDetails?.departmentsDetails && Array.isArray(employeeDetails.departmentsDetails)) {
                deptCount = employeeDetails.departmentsDetails.length;
              } else if (employeeDetails?.department && Array.isArray(employeeDetails.department)) {
                deptCount = employeeDetails.department.length;
              } else if (employeeDetails?.departments && Array.isArray(employeeDetails.departments)) {
                deptCount = employeeDetails.departments.length;
              } else {
                deptCount = 1; // Single department
              }

              return deptCount > 1;
            })(),
            type: leave.leaveType || "Leave",
            startDate: leave.fromDate,
            endDate: leave.toDate,
            days: daysDiff > 0 ? daysDiff : 1,
            duration: `${daysDiff > 0 ? daysDiff : 1} day${daysDiff > 1 ? "s" : ""
              }`,
            status: leave.status || "pending",
            reason: leave.reason || "No reason provided",
            appliedOn: formattedAppliedDate,
            attachment: leave.attachment || null,
            rejectionRemarks: leave.rejectionRemarks || null,
            rejectionReason: leave.rejectionReason || leave.rejectionRemarks || null, // Handle both fields
            contact:
              employeeDetails?.emergencyMobileNumber ||
              employeeDetails?.phone ||
              employeeDetails?.contact ||
              leave.employee?.phone ||
              leave.employee?.emergencyMobileNumber ||
              leave.employee?.contact ||
              "N/A",
            emergencyMobileNumber:
              employeeDetails?.emergencyMobileNumber ||
              leave.employee?.emergencyMobileNumber ||
              "N/A",
            email:
              employeeDetails?.email ||
              leave.employee?.email ||
              "N/A",
            fullEmployeeDetails: employeeDetails,
          };
        })
      );

      setLeaveRequests(formattedRequests);
      console.log("Formatted leave requests:", formattedRequests);
    } catch (err) {
      console.error("Error fetching leave requests:", err);
      setError("Failed to load leave requests. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  // Fetch leave requests on component mount
  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  // Handle approval action - show dates modal for partial approval
  const handleApprove = (id) => {
    const request = leaveRequests.find(req => req.id === id);
    if (request) {
      setApprovingRequestId(id);
      setApprovingRequest(request);

      // Generate all dates between start and end date
      const startDate = new Date(request.startDate);
      const endDate = new Date(request.endDate);
      const allDates = [];

      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        allDates.push(new Date(date));
      }

      // Initially select all dates (full approval)
      setSelectedApprovedDates(allDates);
      setShowApprovedDatesModal(true);
    }
  };

  // Handle approval with selected dates
  const handleApproveWithDates = async () => {
    if (selectedApprovedDates.length === 0) {
      setShowAlert(' /Please select at least one date to approve');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log(`Approving leave request ${approvingRequestId} with dates...`, selectedApprovedDates);

      const response = await axios.put(
        `https://slateblue-mallard-430836.hostingersite.com/api/leaves/${approvingRequestId}/status`,
        {
          status: 'Approved',
          approvedDates: selectedApprovedDates.map(date => date.toISOString())
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Approve with dates response:', response.data);

      // Update local state with approved status and dates
      setLeaveRequests(prevRequests =>
        prevRequests.map(request =>
          request.id === approvingRequestId ? {
            ...request,
            status: 'Approved',
            approvedDates: selectedApprovedDates
          } : request
        )
      );

      // Close modal and reset state
      setShowApprovedDatesModal(false);
      setApprovingRequestId(null);
      setApprovingRequest(null);
      setSelectedApprovedDates([]);

      // Show success message
      const approvalType = selectedApprovedDates.length === getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).length
        ? 'Full approval'
        : `Partial approval (${selectedApprovedDates.length} days)`;
      setShowAlert(`Leave request approved successfully! ${approvalType}`);
    } catch (err) {
      console.error('Error approving leave with dates:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to approve leave. Please try again.');
      setShowAlert(`Failed to approve leave request: ${err.response?.data?.message || err.message}`);
    }
  };

  // Handle cancel approval
  const handleCancelApproval = () => {
    setShowApprovedDatesModal(false);
    setApprovingRequestId(null);
    setApprovingRequest(null);
    setSelectedApprovedDates([]);
  };

  // Helper function to get all dates between two dates
  const getAllDatesBetween = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(new Date(date));
    }

    return dates;
  };

  // Handle date selection toggle
  const handleDateToggle = (date) => {
    const dateString = date.toDateString();
    const isSelected = selectedApprovedDates.some(d => d.toDateString() === dateString);

    if (isSelected) {
      setSelectedApprovedDates(prev => prev.filter(d => d.toDateString() !== dateString));
    } else {
      setSelectedApprovedDates(prev => [...prev, date]);
    }
  };

  // Handle select all dates
  const handleSelectAllDates = () => {
    if (approvingRequest) {
      const allDates = getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate);
      setSelectedApprovedDates(allDates);
    }
  };

  // Handle clear all dates
  const handleClearAllDates = () => {
    setSelectedApprovedDates([]);
  };

  // Handle rejection action - show remarks modal
  const handleReject = (id) => {
    setRejectingRequestId(id);
    setRejectionRemarks('');
    setShowRejectModal(true);
  };

  // Handle rejection with remarks
  const handleRejectWithRemarks = async () => {
    if (!rejectionRemarks.trim()) {
      setShowAlert('Please provide rejection remarks');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log(`Rejecting leave request ${rejectingRequestId} with remarks...`);
      console.log('Rejection reason being stored:', rejectionRemarks.trim());

      const response = await axios.put(
        `https://slateblue-mallard-430836.hostingersite.com/api/leaves/${rejectingRequestId}/status`,
        {
          status: 'Rejected',
          rejectionRemarks: rejectionRemarks.trim(),
          rejectionReason: rejectionRemarks.trim() // Store in rejectionReason field as well
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Reject response:', response.data);
      console.log('✅ Successfully stored rejection reason in database:', {
        rejectionReason: rejectionRemarks.trim(),
        rejectionRemarks: rejectionRemarks.trim(),
        leaveId: rejectingRequestId
      });

      // Update local state with rejected status and remarks
      setLeaveRequests(prevRequests =>
        prevRequests.map(request =>
          request.id === rejectingRequestId ? {
            ...request,
            status: 'Rejected',
            rejectionRemarks: rejectionRemarks.trim(),
            rejectionReason: rejectionRemarks.trim() // Update rejectionReason field as well
          } : request
        )
      );

      // Close modal and reset state
      setShowRejectModal(false);
      setRejectingRequestId(null);
      setRejectionRemarks('');

      // Show success message
      setShowAlert('✅ Leave request rejected successfully!\n Rejection reason stored in database as "rejectionReason" field.');
    } catch (err) {
      console.error('Error rejecting leave:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to reject leave. Please try again.');
      setShowAlert(`Failed to reject leave request: ${err.response?.data?.message || err.message}`);
    }
  };

  // Handle cancel rejection
  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectingRequestId(null);
    setRejectionRemarks('');
  };

  // Handle view details
  const handleViewDetails = (request) => {
    setSelectedRequest(request);
  };

  // Close details view
  const handleCloseDetails = () => {
    setSelectedRequest(null);
  };

  // Handle show more departments
  const handleShowMoreDepartments = (request) => {
    // Extract all department information for this employee
    const employeeDetails = request.fullEmployeeDetails;
    let allDepartments = [];

    // Collect departments from various sources
    if (employeeDetails?.departmentDetails) {
      if (Array.isArray(employeeDetails.departmentDetails)) {
        allDepartments = employeeDetails.departmentDetails;
      } else {
        allDepartments = [employeeDetails.departmentDetails];
      }
    } else if (employeeDetails?.departmentsDetails) {
      allDepartments = employeeDetails.departmentsDetails;
    } else if (employeeDetails?.department) {
      if (Array.isArray(employeeDetails.department)) {
        allDepartments = employeeDetails.department.map(dept =>
          typeof dept === 'object' ? dept : { name: dept, _id: dept }
        );
      } else if (typeof employeeDetails.department === 'object') {
        allDepartments = [employeeDetails.department];
      }
    } else if (employeeDetails?.departments) {
      allDepartments = employeeDetails.departments.map(dept =>
        typeof dept === 'object' ? dept : { name: dept, _id: dept }
      );
    }

    setSelectedDepartments({
      employeeName: request.teamMemberName,
      departments: allDepartments
    });
    setShowDepartmentModal(true);
  };

  // Close department modal
  const handleCloseDepartmentModal = () => {
    setShowDepartmentModal(false);
    setSelectedDepartments([]);
  };

  // Filter and search leave requests
  const filteredRequests = leaveRequests.filter(request => {
    // Apply filters
    const statusMatch = filters.status === 'All' || request.status === filters.status;

    // Check if employee belongs to selected department (check all departments, not just first)
    const departmentMatch = filters.department === 'All' || (() => {
      const employeeDetails = request.fullEmployeeDetails;

      // If no employee details, fallback to simple match
      if (!employeeDetails) {
        return request.department === filters.department;
      }

      // Collect all department names for this employee
      let allDepartmentNames = [];

      // Check departmentDetails (single or array)
      if (employeeDetails.departmentDetails) {
        if (Array.isArray(employeeDetails.departmentDetails)) {
          allDepartmentNames = employeeDetails.departmentDetails.map(dept => dept?.name || dept?.departmentName).filter(Boolean);
        } else {
          const deptName = employeeDetails.departmentDetails.name || employeeDetails.departmentDetails.departmentName;
          if (deptName) allDepartmentNames.push(deptName);
        }
      }

      // Check departmentsDetails (array)
      if (employeeDetails.departmentsDetails && Array.isArray(employeeDetails.departmentsDetails)) {
        const names = employeeDetails.departmentsDetails.map(dept => dept?.name || dept?.departmentName).filter(Boolean);
        allDepartmentNames = [...allDepartmentNames, ...names];
      }

      // If we found department names, check if any match the filter
      if (allDepartmentNames.length > 0) {
        return allDepartmentNames.includes(filters.department);
      }

      // Fallback to simple match with first department
      return request.department === filters.department;
    })();

    // Apply search query
    const searchMatch = searchQuery === '' ||
      request.teamMemberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.reason.toLowerCase().includes(searchQuery.toLowerCase());

    return statusMatch && departmentMatch && searchMatch;
  });

  // Count requests by status (case insensitive)
  const pendingCount = leaveRequests.filter(req =>
    req.status && req.status.toLowerCase() === 'pending'
  ).length;
  const approvedCount = leaveRequests.filter(req =>
    req.status && req.status.toLowerCase() === 'approved'
  ).length;
  const rejectedCount = leaveRequests.filter(req =>
    req.status && req.status.toLowerCase() === 'rejected'
  ).length;

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#4caf50';
      case 'Pending': return '#ff9800';
      case 'Rejected': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  // Fetch departments for the selected company

  useEffect(() => {
    const fetchDepartments = async () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");

      if (!token) {
        console.error("No access token found. Please log in.");
        return;
      }

      let companyId;
      try {
        const decoded = jwtDecode(token);
        companyId = decoded.companyId || decoded.id; // adjust based on your backend payload
      } catch (err) {
        console.error("Error decoding token:", err);
        return;
      }

      if (!companyId) {
        console.error("No companyId found in token.");
        return;
      }

      try {
        const response = await axios.get(
          `https://slateblue-mallard-430836.hostingersite.com/api/departments/company/${companyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && Array.isArray(response.data)) {
          // Extract department names from the response
          const departmentNames = response.data.map(
            (dept) => dept.name || dept.departmentName
          );
          setDepartments(["All", ...new Set(departmentNames)]);
        } else {
          // Fallback to unique departments from leave requests
          const uniqueDepts = [
            "All",
            ...new Set(leaveRequests.map((req) => req.department)),
          ];
          setDepartments(
            uniqueDepts.filter((dept) => dept !== null && dept !== undefined)
          );
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        // Fallback to unique departments from leave requests if API call fails
        const uniqueDepts = [
          "All",
          ...new Set(leaveRequests.map((req) => req.department)),
        ];
        setDepartments(
          uniqueDepts.filter((dept) => dept !== null && dept !== undefined)
        );
      }
    };

    fetchDepartments();
  }, [leaveRequests]);



  return (
    <div style={styles.container}>
      {/* Loading and Error States */}
      {loading && <div style={styles.loading}>Loading leave requests...</div>}
      {error && <div style={styles.error}>{error}</div>}

      {/* Stats Overview */}
      <div style={styles.statsContainer}>
        <div
          style={{
            ...styles.statCard,
            borderBottom: filters.status === 'Pending' ? '4px solid #ff9800' : 'none',
            opacity: filters.status === 'All' || filters.status === 'Pending' ? 1 : 0.6,
            cursor: 'pointer'
          }}
          onClick={() => handleFilterChange('status', filters.status === 'Pending' ? 'All' : 'Pending')}
        >
          <h3 style={styles.statNumber}>{pendingCount}</h3>
          <p style={styles.statLabel}>Pending Requests</p>
        </div>
        <div
          style={{
            ...styles.statCard,
            borderBottom: filters.status === 'Approved' ? '4px solid #4caf50' : 'none',
            opacity: filters.status === 'All' || filters.status === 'Approved' ? 1 : 0.6,
            cursor: 'pointer'
          }}
          onClick={() => handleFilterChange('status', filters.status === 'Approved' ? 'All' : 'Approved')}
        >
          <h3 style={styles.statNumber}>{approvedCount}</h3>
          <p style={styles.statLabel}>Approved</p>
        </div>
        <div
          style={{
            ...styles.statCard,
            borderBottom: filters.status === 'Rejected' ? '4px solid #f44336' : 'none',
            opacity: filters.status === 'All' || filters.status === 'Rejected' ? 1 : 0.6,
            cursor: 'pointer'
          }}
          onClick={() => handleFilterChange('status', filters.status === 'Rejected' ? 'All' : 'Rejected')}
        >
          <h3 style={styles.statNumber}>{rejectedCount}</h3>
          <p style={styles.statLabel}>Rejected</p>
        </div>
        <div
          style={{
            ...styles.statCard,
            borderBottom: filters.status === 'All' ? '4px solid #3498db' : 'none',
            cursor: 'pointer'
          }}
          onClick={() => handleFilterChange('status', 'All')}
        >
          <h3 style={styles.statNumber}>{leaveRequests.length}</h3>
          <p style={styles.statLabel}>Total Requests</p>
        </div>
      </div>

      {/* Controls Row - Search and Filters merged */}
      <div style={styles.controlsContainer}>
        <div style={styles.searchBoxMerged}>
          <span style={styles.searchIconMerged}></span>
          <input
            type="text"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={styles.searchInputMerged}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.clearSearchButtonMerged}>×</button>
          )}
        </div>

        <div style={styles.filterGroupMerged}>
          <select
            value={filters.department}
            onChange={(e) => handleFilterChange('department', e.target.value)}
            style={styles.filterSelectMerged}
          >
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept === 'All' ? 'All Departments' : dept}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroupMerged}>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={styles.filterSelectMerged}
          >
            <option value="All">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Leave Requests Table */}
      <div style={styles.requestsContainer}>
        <div style={styles.tableHeader}>
          <h3 style={styles.sectionHeading}>Leave Requests</h3>
          <div style={styles.resultsCount}>
            Showing {filteredRequests.length} of {leaveRequests.length} requests
          </div>
        </div>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Employee</th>
                <th style={styles.tableHeader}>Department</th>
                <th style={styles.tableHeader}>Leave Type</th>
                <th style={styles.tableHeader}>Duration</th>
                <th style={styles.tableHeader}>Applied On</th>
                <th style={styles.tableHeader}>Status</th>
                <th style={styles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map(request => (
                <tr key={request.id} style={styles.tableRow}>
                  <td style={styles.tableCell}>
                    <div style={styles.employeeInfo}>
                      <div style={styles.teamMemberName}>{request.teamMemberName}</div>
                      {/* <div style={styles.employeeId}>{request.employeeId}</div> */}
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.departmentCell}>
                      <span style={styles.departmentText}>{request.department}</span>
                      {request.hasMultipleDepartments && (
                        <button
                          onClick={() => handleShowMoreDepartments(request)}
                          style={styles.showMoreButton}
                          title="View all departments"
                        >
                          Show More
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={styles.tableCell}>{request.type}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.durationInfo}>
                      <div style={styles.dateRange}>
                        {new Date(request.startDate).toLocaleDateString('en-GB')} to {new Date(request.endDate).toLocaleDateString('en-GB')}
                      </div>
                      <div style={styles.daysBadge}>{request.duration}</div>
                    </div>
                  </td>
                  <td style={styles.tableCell}>{request.appliedOn}</td>
                  <td style={styles.tableCell}>
                    <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(request.status) }}>
                      {request.status}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleViewDetails(request)}
                        style={styles.detailsButton}
                      >
                        View
                      </button>
                      {request.status === 'Pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(request.id)}
                            style={styles.approveButton}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            style={styles.rejectButton}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRequests.length === 0 && (
            <div style={styles.noResults}>
              <p>No leave requests match the selected filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Leave Request Details</h3>
              <button onClick={handleCloseDetails} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.detailSection}>
                <h4 style={styles.detailHeading}>Employee Information</h4>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Name:</span>
                    <span style={styles.detailValue}>{selectedRequest.teamMemberName}</span>
                  </div>
                  {/* <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>ID:</span>
                    <span style={styles.detailValue}>{selectedRequest.employeeId}</span>
                  </div> */}
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Department:</span>
                    <span style={styles.detailValue}>{selectedRequest.department}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Emergency Mobile:</span>
                    <span style={styles.detailValue}>{selectedRequest.emergencyMobileNumber}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Email:</span>
                    <span style={styles.detailValue}>{selectedRequest.email}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Contact:</span>
                    <span style={styles.detailValue}>{selectedRequest.contact}</span>
                  </div>
                </div>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailHeading}>Leave Information</h4>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Type:</span>
                    <span style={styles.detailValue}>{selectedRequest.type}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Duration:</span>
                    <span style={styles.detailValue}>
                      <div style={styles.durationDetails}>
                        <div style={styles.dateRangeDetail}>
                          From: {new Date(selectedRequest.startDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                        <div style={styles.dateRangeDetail}>
                          To: {new Date(selectedRequest.endDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                        <div style={styles.totalDays}>
                          Total: {selectedRequest.duration}
                        </div>
                      </div>
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Applied On:</span>
                    <span style={styles.detailValue}>{selectedRequest.appliedOn}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Status:</span>
                    <span style={styles.detailValue}>
                      <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(selectedRequest.status) }}>
                        {selectedRequest.status}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailHeading}>Reason</h4>
                <p style={styles.reasonText}>{selectedRequest.reason}</p>
              </div>

              {selectedRequest.status === 'Rejected' && (selectedRequest.rejectionRemarks || selectedRequest.rejectionReason) && (
                <div style={styles.detailSection}>
                  <h4 style={styles.detailHeading}>š Rejection Reason </h4>
                  <p style={styles.rejectionRemarksText}>
                    {selectedRequest.rejectionReason || selectedRequest.rejectionRemarks}
                  </p>
                  <div style={styles.rejectionInfo}>
                    <small style={styles.rejectionLabel}>
                      Stored in database as: rejectionReason field
                    </small>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'Approved' && selectedRequest.approvedDates && (
                <div style={styles.detailSection}>
                  <h4 style={styles.detailHeading}>✅ Approved Dates </h4>
                  <div style={styles.approvedDatesContainer}>
                    <div style={styles.approvedDatesInfo}>
                      <div style={styles.approvedDatesCount}>
                        <span style={styles.approvedCountLabel}>Approved Days:</span>
                        <span style={styles.approvedCountValue}>{selectedRequest.approvedDates.length} out of {selectedRequest.days}</span>
                      </div>
                      <div style={styles.approvalTypeIndicator}>
                        <span style={{
                          ...styles.approvalTypeBadge,
                          backgroundColor: selectedRequest.approvedDates.length === selectedRequest.days ? '#2ecc71' : '#f39c12'
                        }}>
                          {selectedRequest.approvedDates.length === selectedRequest.days
                            ? ' / Full Approval'
                            : '/ Partial Approval'
                          }
                        </span>
                      </div>
                    </div>
                    <div style={styles.approvedDatesList}>
                      {selectedRequest.approvedDates.map((dateStr, index) => {
                        const date = new Date(dateStr);
                        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                        return (
                          <div key={index} style={{
                            ...styles.approvedDateItem,
                            ...(isWeekend ? styles.weekendApprovedDate : {})
                          }}>
                            <div style={styles.approvedDateNumber}>{date.getDate()}</div>
                            <div style={styles.approvedDateDay}>{dayName}</div>
                            <div style={styles.approvedDateMonth}>
                              {date.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {selectedRequest.attachment && (
                <div style={styles.detailSection}>
                  <h4 style={styles.detailHeading}>Attachment</h4>
                  <div style={styles.attachment}>
                    <span style={styles.attachmentIcon}>Ž</span>
                    <a href="#" style={styles.attachmentLink}>{selectedRequest.attachment}</a>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {selectedRequest.status === 'Pending' && (
                <>
                  <button
                    onClick={() => handleApprove(selectedRequest.id)}
                    style={styles.modalApproveButton}
                  >
                    Approve Request
                  </button>
                  <button
                    onClick={() => handleReject(selectedRequest.id)}
                    style={styles.modalRejectButton}
                  >
                    Reject Request
                  </button>
                </>
              )}
              <button
                onClick={handleCloseDetails}
                style={styles.modalCloseButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department Details Modal */}
      {showDepartmentModal && selectedDepartments && (
        <div style={styles.modalOverlay}>
          <div style={styles.departmentModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                📋 All Departments - {selectedDepartments.employeeName}
              </h3>
              <button onClick={handleCloseDepartmentModal} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.departmentList}>
                {selectedDepartments.departments && selectedDepartments.departments.length > 0 ? (
                  selectedDepartments.departments.map((dept, index) => (
                    <div key={index} style={styles.departmentItem}>
                      <div style={styles.departmentIcon}></div>
                      <div style={styles.departmentInfo}>
                        <div style={styles.departmentName}>
                          {dept?.name || dept?.departmentName || dept || 'Unknown Department'}
                        </div>
                        {dept?._id && (
                          <div style={styles.departmentId}>ID: {dept._id}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.noDepartments}>
                    <span style={styles.noDataIcon}>­</span>
                    <p>No department information available</p>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={handleCloseDepartmentModal}
                style={styles.modalCloseButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Remarks Modal */}
      {showRejectModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.rejectModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                š Leave Rejection
              </h3>
              <button onClick={handleCancelReject} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.rejectSection}>
                <p style={styles.rejectInstruction}>
                  Please enter leave reject reason:<br />
                  <span style={styles.englishText}>Please provide reason for rejecting this leave request:</span>
                </p>

                <div style={styles.dbInfoBox}>
                  <small style={styles.dbInfoText}>
                    This reason will be stored in database as "rejectionReason" field
                  </small>
                </div>

                <textarea
                  value={rejectionRemarks}
                  onChange={(e) => setRejectionRemarks(e.target.value)}
                  placeholder="e.g: Work pressure, insufficient staff, etc..."
                  style={styles.remarksTextarea}
                  rows={4}
                  maxLength={500}
                />

                <div style={styles.characterCount}>
                  {rejectionRemarks.length}/500 characters
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={handleRejectWithRemarks}
                style={styles.confirmRejectButton}
                disabled={!rejectionRemarks.trim()}
              >
                š Confirm Rejection
              </button>
              <button
                onClick={handleCancelReject}
                style={styles.cancelRejectButton}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approved Dates Selection Modal */}
      {showApprovedDatesModal && approvingRequest && (
        <div style={styles.modalOverlay}>
          <div style={styles.approvedDatesModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                ✅ Leave Approval -  Date Selection
              </h3>
              <button onClick={handleCancelApproval} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.approvalSection}>
                <div style={styles.employeeInfoSection}>
                  <h4 style={styles.sectionTitle}>📋 Employee Details</h4>
                  <div style={styles.employeeDetails}>
                    <span style={styles.employeeName}>{approvingRequest.teamMemberName}</span>
                    <span style={styles.leaveDuration}>
                      {new Date(approvingRequest.startDate).toLocaleDateString('en-GB')} to {new Date(approvingRequest.endDate).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>

                <div style={styles.instructionSection}>
                  <p style={styles.approvalInstruction}>
                    <br />
                    <span style={styles.englishText}>Please select the dates to approve for this leave request:</span>
                  </p>
                </div>

                <div style={styles.dateSelectionControls}>
                  <button onClick={handleSelectAllDates} style={styles.selectAllButton}>
                    ✅ Select All ({getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).length} days)
                  </button>
                  <button onClick={handleClearAllDates} style={styles.clearAllButton}>
                    ❌ Clear All
                  </button>
                  <div style={styles.selectedCount}>
                    Selected: {selectedApprovedDates.length} days
                  </div>
                </div>

                <div style={styles.datesGrid}>
                  {getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).map((date, index) => {
                    const isSelected = selectedApprovedDates.some(d => d.toDateString() === date.toDateString());
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                    return (
                      <div
                        key={index}
                        onClick={() => handleDateToggle(date)}
                        style={{
                          ...styles.dateCard,
                          ...(isSelected ? styles.selectedDateCard : {}),
                          ...(isWeekend ? styles.weekendDateCard : {})
                        }}
                      >
                        <div style={styles.dateNumber}>
                          {date.getDate()}
                        </div>
                        <div style={styles.dayName}>
                          {dayName}
                        </div>
                        <div style={styles.monthYear}>
                          {date.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                        {isSelected && (
                          <div style={styles.checkmark}>✅</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={styles.approvalSummary}>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>Total Requested Days:</span>
                    <span style={styles.summaryValue}>{getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).length}</span>
                  </div>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>Approved Days:</span>
                    <span style={styles.summaryValue}>{selectedApprovedDates.length}</span>
                  </div>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>Approval Type:</span>
                    <span style={{
                      ...styles.summaryValue,
                      color: selectedApprovedDates.length === getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).length ? '#2ecc71' : '#f39c12'
                    }}>
                      {selectedApprovedDates.length === getAllDatesBetween(approvingRequest.startDate, approvingRequest.endDate).length
                        ? 'Full Approval'
                        : 'Partial Approval'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={handleApproveWithDates}
                style={styles.confirmApprovalButton}
                disabled={selectedApprovedDates.length === 0}
              >
                ✅ Confirm Approval ({selectedApprovedDates.length} days)
              </button>
              <button
                onClick={handleCancelApproval}
                style={styles.cancelApprovalButton}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline CSS styles
const styles = {
  container: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    padding: '10px 15px',
    backgroundColor: '#f1f5f9',
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%'
  },
  loading: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    fontSize: '16px',
    margin: '20px 0'
  },
  error: {
    padding: '15px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '4px',
    margin: '15px 0',
    textAlign: 'center',
    borderLeft: '4px solid #c62828'
  },
  heading: {
    color: '#2c3e50',
    marginBottom: '25px',
    fontSize: '28px',
    fontWeight: '600',
    borderBottom: '2px solid #3498db',
    paddingBottom: '10px'
  },
  statsContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    flexWrap: 'wrap'
  },
  statCard: {
    flex: '1',
    minWidth: '150px',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '12px 10px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease'
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: '800',
    margin: '0 0 4px 0',
    color: '#2c3e50'
  },
  statLabel: {
    margin: '0',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  controlsContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '10px 15px',
    marginBottom: '20px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'nowrap',
    width: '100%'
  },
  searchBoxMerged: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: '2.5',
    minWidth: '200px'
  },
  searchIconMerged: {
    position: 'absolute',
    left: '12px',
    fontSize: '14px',
    color: '#64748b'
  },
  searchInputMerged: {
    width: '100%',
    padding: '8px 35px 8px 35px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: '#f8fafc'
  },
  clearSearchButtonMerged: {
    position: 'absolute',
    right: '10px',
    backgroundColor: '#e2e8f0',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#64748b',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterGroupMerged: {
    flex: '1',
    minWidth: '130px'
  },
  filterSelectMerged: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    backgroundColor: '#f8fafc',
    outline: 'none',
    cursor: 'pointer',
    color: '#475569',
    fontWeight: '500'
  },
  requestsContainer: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionHeading: {
    margin: '0',
    color: '#2c3e50',
    fontSize: '20px'
  },
  resultsCount: {
    color: '#7f8c8d',
    fontSize: '14px'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    backgroundColor: '#f1f2f6',
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#2c3e50',
    borderBottom: '2px solid #dfe4ea'
  },
  tableRow: {
    borderBottom: '1px solid #dfe4ea'
  },
  tableCell: {
    padding: '12px 15px',
    verticalAlign: 'top'
  },
  employeeInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  teamMemberName: {
    fontWeight: '600',
    color: '#2c3e50'
  },
  employeeId: {
    fontSize: '12px',
    color: '#7f8c8d'
  },
  durationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  dateRange: {
    fontSize: '14px',
    color: '#2c3e50'
  },
  daysBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
    alignSelf: 'flex-start'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  detailsButton: {
    padding: '6px 12px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  approveButton: {
    padding: '6px 12px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  rejectButton: {
    padding: '6px 12px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  noResults: {
    textAlign: 'center',
    padding: '40px',
    color: '#7f8c8d'
  },
  modalOverlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
    padding: '20px'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #dfe4ea'
  },
  modalTitle: {
    margin: '0',
    color: '#2c3e50'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#7f8c8d'
  },
  modalContent: {
    padding: '20px'
  },
  detailSection: {
    marginBottom: '25px'
  },
  detailHeading: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '16px'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '15px'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column'
  },
  detailLabel: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontWeight: '600',
    marginBottom: '5px'
  },
  detailValue: {
    fontSize: '14px',
    color: '#2c3e50'
  },
  reasonText: {
    margin: '0',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    borderLeft: '4px solid #3498db'
  },
  attachment: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  attachmentIcon: {
    fontSize: '18px'
  },
  attachmentLink: {
    color: '#3498db',
    textDecoration: 'none',
    fontWeight: '600'
  },
  daysText: {
    color: '#7f8c8d',
    fontSize: '12px',
    marginLeft: '5px'
  },
  durationDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  dateRangeDetail: {
    fontSize: '14px',
    color: '#2c3e50'
  },
  totalDays: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1976d2',
    backgroundColor: '#e3f2fd',
    padding: '2px 8px',
    borderRadius: '12px',
    display: 'inline-block',
    marginTop: '4px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '20px',
    borderTop: '1px solid #dfe4ea'
  },
  modalApproveButton: {
    padding: '10px 20px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  modalRejectButton: {
    padding: '10px 20px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  modalCloseButton: {
    padding: '10px 20px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  // Department cell styles
  departmentCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  departmentText: {
    fontSize: '14px',
    color: '#2c3e50'
  },
  showMoreButton: {
    padding: '4px 8px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#2980b9'
    }
  },
  // Department modal styles
  departmentModal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  departmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  departmentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  departmentIcon: {
    fontSize: '20px',
    minWidth: '24px'
  },
  departmentInfo: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  departmentName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  departmentId: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontFamily: 'monospace'
  },
  noDepartments: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#7f8c8d'
  },
  noDataIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px'
  },
  // Rejection modal styles
  rejectModal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  rejectSection: {
    marginBottom: '20px'
  },
  rejectInstruction: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.5'
  },
  englishText: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '400'
  },
  remarksTextarea: {
    width: '100%',
    padding: '12px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    outline: 'none',
    transition: 'border-color 0.3s',
    ':focus': {
      borderColor: '#e74c3c'
    }
  },
  characterCount: {
    textAlign: 'right',
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '5px'
  },
  confirmRejectButton: {
    padding: '12px 24px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#c0392b'
    },
    ':disabled': {
      backgroundColor: '#bdc3c7',
      cursor: 'not-allowed'
    }
  },
  cancelRejectButton: {
    padding: '12px 24px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#7f8c8d'
    }
  },
  rejectionRemarksText: {
    margin: '0',
    padding: '15px',
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    borderLeft: '4px solid #e74c3c',
    color: '#c62828',
    fontStyle: 'italic'
  },
  rejectionInfo: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    borderLeft: '3px solid #2196f3'
  },
  rejectionLabel: {
    color: '#555',
    fontStyle: 'normal',
    fontSize: '12px'
  },
  dbInfoBox: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    padding: '8px 12px',
    marginBottom: '12px'
  },
  dbInfoText: {
    color: '#1976d2',
    fontSize: '12px',
    fontWeight: '500'
  },
  // Approved dates modal styles
  approvedDatesModal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  approvalSection: {
    marginBottom: '20px'
  },
  employeeInfoSection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  sectionTitle: {
    margin: '0 0 10px 0',
    color: '#2c3e50',
    fontSize: '16px',
    fontWeight: '600'
  },
  employeeDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  employeeName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  leaveDuration: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '500'
  },
  instructionSection: {
    marginBottom: '20px'
  },
  approvalInstruction: {
    margin: '0',
    color: '#2c3e50',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.5'
  },
  dateSelectionControls: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    flexWrap: 'wrap'
  },
  selectAllButton: {
    padding: '8px 16px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#27ae60'
    }
  },
  clearAllButton: {
    padding: '8px 16px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#c0392b'
    }
  },
  selectedCount: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3498db',
    padding: '8px 12px',
    backgroundColor: '#e3f2fd',
    borderRadius: '6px'
  },
  datesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '12px',
    marginBottom: '25px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    maxHeight: '300px',
    overflowY: 'auto'
  },
  dateCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 8px',
    backgroundColor: 'white',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    minHeight: '80px',
    justifyContent: 'center',
    ':hover': {
      borderColor: '#3498db',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
  },
  selectedDateCard: {
    backgroundColor: '#e8f5e8',
    borderColor: '#2ecc71',
    boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)'
  },
  weekendDateCard: {
    backgroundColor: '#fff3e0',
    borderColor: '#ff9800'
  },
  dateNumber: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: '4px'
  },
  dayName: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontWeight: '600',
    marginBottom: '2px'
  },
  monthYear: {
    fontSize: '10px',
    color: '#95a5a6',
    fontWeight: '500'
  },
  checkmark: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: '#2ecc71',
    color: 'white',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700'
  },
  approvalSummary: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e9ecef'
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '600'
  },
  summaryValue: {
    fontSize: '14px',
    color: '#2c3e50',
    fontWeight: '700'
  },
  confirmApprovalButton: {
    padding: '12px 24px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#27ae60'
    },
    ':disabled': {
      backgroundColor: '#bdc3c7',
      cursor: 'not-allowed'
    }
  },
  cancelApprovalButton: {
    padding: '12px 24px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#7f8c8d'
    }
  },
  // Approved dates display styles
  approvedDatesContainer: {
    padding: '15px',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bfdbfe'
  },
  approvedDatesInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  approvedDatesCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  approvedCountLabel: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '600'
  },
  approvedCountValue: {
    fontSize: '14px',
    color: '#2c3e50',
    fontWeight: '700',
    padding: '4px 8px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px'
  },
  approvalTypeIndicator: {
    display: 'flex',
    alignItems: 'center'
  },
  approvalTypeBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  approvedDatesList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
    gap: '8px',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  approvedDateItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 6px',
    backgroundColor: 'white',
    border: '2px solid #2ecc71',
    borderRadius: '6px',
    minHeight: '60px',
    justifyContent: 'center'
  },
  weekendApprovedDate: {
    backgroundColor: '#fff3e0',
    borderColor: '#ff9800'
  },
  approvedDateNumber: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: '2px'
  },
  approvedDateDay: {
    fontSize: '10px',
    color: '#7f8c8d',
    fontWeight: '600',
    marginBottom: '1px'
  },
  approvedDateMonth: {
    fontSize: '9px',
    color: '#95a5a6',
    fontWeight: '500'
  }
};

// Add hover effects
Object.assign(styles.searchInputMerged, {
  ':focus': {
    borderColor: '#3498db',
    boxShadow: '0 0 0 3px rgba(52, 152, 219, 0.2)'
  }
});

Object.assign(styles.clearSearchButtonMerged, {
  ':hover': {
    backgroundColor: '#f1f2f6'
  }
});

export default LeaveApproval;

